import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { dashboardApi } from '../lib/api';
import clsx from 'clsx';
import { StatusPieChart, PriorityBarChart, TypeBarChart } from '../components/dashboard/Charts';
import SLAAlert from '../components/dashboard/SLAAlert';

function StatCard({
  title,
  value,
  color = 'primary',
}: {
  title: string;
  value: number | string;
  color?: 'primary' | 'green' | 'yellow' | 'red' | 'purple';
}) {
  const colorClasses = {
    primary: 'bg-primary-50 text-primary-700',
    green: 'bg-green-50 text-green-700',
    yellow: 'bg-yellow-50 text-yellow-700',
    red: 'bg-red-50 text-red-700',
    purple: 'bg-purple-50 text-purple-700',
  };

  return (
    <div className="card p-4">
      <p className="text-sm text-gray-500">{title}</p>
      <p className={clsx('text-3xl font-bold mt-1', colorClasses[color])}>
        {value}
      </p>
    </div>
  );
}

function TimeUsageGauge({ data }: { data: any }) {
  const percentage = Math.min(data.usagePercentage, 100);
  const isOvertime = data.overtimeHours > 0;

  return (
    <div className="card p-6">
      <h3 className="text-lg font-semibold mb-4">월간 시간 사용량</h3>

      <div className="relative pt-1">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-600">
            {data.usedHours.toFixed(1)}h / {data.availableHours.toFixed(1)}h
          </span>
          <span className={clsx(
            'text-sm font-medium',
            isOvertime ? 'text-red-600' : 'text-green-600'
          )}>
            {percentage.toFixed(1)}%
          </span>
        </div>

        <div className="overflow-hidden h-3 text-xs flex rounded-full bg-gray-200">
          <div
            style={{ width: `${Math.min(percentage, 100)}%` }}
            className={clsx(
              'shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center rounded-full transition-all',
              percentage >= 100 ? 'bg-red-500' :
              percentage >= 75 ? 'bg-yellow-500' : 'bg-green-500'
            )}
          />
        </div>

        <div className="flex justify-between mt-4 text-sm">
          <div>
            <span className="text-gray-500">잔여: </span>
            <span className="font-medium">{data.remainingHours.toFixed(1)}h</span>
          </div>
          {data.rolloverIn > 0 && (
            <div>
              <span className="text-gray-500">이월: </span>
              <span className="font-medium text-blue-600">+{data.rolloverIn.toFixed(1)}h</span>
            </div>
          )}
          {isOvertime && (
            <div>
              <span className="text-gray-500">초과: </span>
              <span className="font-medium text-red-600">{data.overtimeHours.toFixed(1)}h</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PriorityBadge({ priority }: { priority: string }) {
  const colors: Record<string, string> = {
    P1: 'bg-red-100 text-red-700',
    P2: 'bg-yellow-100 text-yellow-700',
    P3: 'bg-green-100 text-green-700',
  };

  return (
    <span className={clsx('px-2 py-0.5 text-xs font-medium rounded-full', colors[priority])}>
      {priority}
    </span>
  );
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    OPEN: 'bg-gray-100 text-gray-700',
    IN_PROGRESS: 'bg-blue-100 text-blue-700',
    REVIEW: 'bg-purple-100 text-purple-700',
    DONE: 'bg-green-100 text-green-700',
    CLOSED: 'bg-gray-100 text-gray-500',
  };

  const labels: Record<string, string> = {
    OPEN: '대기',
    IN_PROGRESS: '진행중',
    REVIEW: '검토',
    DONE: '완료',
    CLOSED: '종료',
  };

  return (
    <span className={clsx('px-2 py-0.5 text-xs font-medium rounded-full', colors[status])}>
      {labels[status] || status}
    </span>
  );
}

export default function DashboardPage() {
  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey: ['dashboard', 'stats'],
    queryFn: () => dashboardApi.stats(),
  });

  const { data: timeData, isLoading: timeLoading } = useQuery({
    queryKey: ['dashboard', 'time-usage'],
    queryFn: () => dashboardApi.timeUsage(),
  });

  const { data: tasksData, isLoading: tasksLoading } = useQuery({
    queryKey: ['dashboard', 'my-tasks'],
    queryFn: () => dashboardApi.myTasks(),
  });

  const { data: recentData, isLoading: recentLoading } = useQuery({
    queryKey: ['dashboard', 'recent'],
    queryFn: () => dashboardApi.recent(5),
  });

  const { data: warrantyData, isLoading: warrantyLoading } = useQuery({
    queryKey: ['dashboard', 'warranty-expiring'],
    queryFn: () => dashboardApi.warrantyExpiring(),
  });

  const { data: quotesData, isLoading: quotesLoading } = useQuery({
    queryKey: ['dashboard', 'pending-quotes'],
    queryFn: () => dashboardApi.pendingQuotes(),
  });

  const stats = statsData?.data;
  const timeUsage = timeData?.data;
  const myTasks = tasksData?.data || [];
  const recentRequests = recentData?.data || [];
  const warrantyExpiring = warrantyData?.data || [];
  const pendingQuotes = quotesData?.data || [];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">대시보드</h1>

      {/* 통계 카드 */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {statsLoading ? (
          <>
            {[...Array(5)].map((_, i) => (
              <div key={i} className="card p-4 animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-20 mb-2" />
                <div className="h-8 bg-gray-200 rounded w-12" />
              </div>
            ))}
          </>
        ) : (
          <>
            <StatCard title="전체 요청" value={stats?.totalRequests || 0} />
            <StatCard title="대기중" value={stats?.openRequests || 0} color="yellow" />
            <StatCard title="진행중" value={stats?.inProgressRequests || 0} color="primary" />
            <StatCard title="검토" value={stats?.reviewRequests || 0} color="purple" />
            <StatCard title="완료" value={stats?.completedRequests || 0} color="green" />
          </>
        )}
      </div>

      {/* SLA 경고 */}
      <SLAAlert />

      {/* 시간 사용량 */}
      {!timeLoading && timeUsage && (
        <TimeUsageGauge data={timeUsage} />
      )}

      {/* 차트 */}
      {!statsLoading && stats && (
        <div className="grid md:grid-cols-3 gap-6">
          <div className="card p-4">
            <h3 className="font-semibold mb-4">상태별 분포</h3>
            <StatusPieChart data={stats.byStatus || {}} />
          </div>
          <div className="card p-4">
            <h3 className="font-semibold mb-4">우선순위별 분포</h3>
            <PriorityBarChart data={stats.byPriority || {}} />
          </div>
          <div className="card p-4">
            <h3 className="font-semibold mb-4">유형별 분포</h3>
            <TypeBarChart data={stats.byType || {}} />
          </div>
        </div>
      )}

      {/* 하단 그리드 */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* 내 할당 업무 */}
        <div className="card">
          <div className="p-4 border-b border-gray-100">
            <h3 className="font-semibold">내 할당 업무</h3>
          </div>
          <div className="divide-y divide-gray-100">
            {tasksLoading ? (
              <div className="p-4 text-center text-gray-500">로딩중...</div>
            ) : myTasks.length === 0 ? (
              <div className="p-4 text-center text-gray-500">할당된 업무가 없습니다</div>
            ) : (
              myTasks.map((task: any) => (
                <Link
                  key={task.id}
                  to={`/requests/${task.id}`}
                  className="block p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {task.title}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">{task.reqId}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <PriorityBadge priority={task.priority} />
                      <StatusBadge status={task.status} />
                    </div>
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>

        {/* 최근 요청 */}
        <div className="card">
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-semibold">최근 요청</h3>
            <Link to="/requests" className="text-sm text-primary-600 hover:underline">
              전체보기
            </Link>
          </div>
          <div className="divide-y divide-gray-100">
            {recentLoading ? (
              <div className="p-4 text-center text-gray-500">로딩중...</div>
            ) : recentRequests.length === 0 ? (
              <div className="p-4 text-center text-gray-500">요청이 없습니다</div>
            ) : (
              recentRequests.map((req: any) => (
                <Link
                  key={req.id}
                  to={`/requests/${req.id}`}
                  className="block p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {req.title}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {req.reqId} · {req.client}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <PriorityBadge priority={req.priority} />
                      <StatusBadge status={req.status} />
                    </div>
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>
      </div>

      {/* 보증기간 및 견적 위젯 */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* 보증기간 만료 임박 */}
        <div className="card">
          <div className="p-4 border-b border-gray-100 flex items-center gap-2">
            <span className="text-orange-500">⚠️</span>
            <h3 className="font-semibold">보증기간 만료 임박</h3>
            <span className="text-xs text-gray-500">(7일 이내)</span>
          </div>
          <div className="divide-y divide-gray-100">
            {warrantyLoading ? (
              <div className="p-4 text-center text-gray-500">로딩중...</div>
            ) : warrantyExpiring.length === 0 ? (
              <div className="p-4 text-center text-gray-500">만료 임박 건이 없습니다</div>
            ) : (
              warrantyExpiring.map((item: any) => {
                const daysLeft = Math.ceil(
                  (new Date(item.warrantyUntil).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
                );
                return (
                  <Link
                    key={item.id}
                    to={`/requests/${item.id}`}
                    className="block p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {item.title}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {item.reqId} · {item.client}
                        </p>
                      </div>
                      <div className="text-right">
                        <span className={clsx(
                          'text-xs font-medium px-2 py-1 rounded-full',
                          daysLeft <= 2 ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'
                        )}>
                          D-{daysLeft}
                        </span>
                        <p className="text-xs text-gray-400 mt-1">
                          {new Date(item.warrantyUntil).toLocaleDateString('ko-KR')}
                        </p>
                      </div>
                    </div>
                  </Link>
                );
              })
            )}
          </div>
        </div>

        {/* 견적 대기 */}
        <div className="card">
          <div className="p-4 border-b border-gray-100 flex items-center gap-2">
            <span className="text-blue-500">📋</span>
            <h3 className="font-semibold">견적 대기</h3>
          </div>
          <div className="divide-y divide-gray-100">
            {quotesLoading ? (
              <div className="p-4 text-center text-gray-500">로딩중...</div>
            ) : pendingQuotes.length === 0 ? (
              <div className="p-4 text-center text-gray-500">대기중인 견적이 없습니다</div>
            ) : (
              pendingQuotes.map((item: any) => (
                <Link
                  key={item.id}
                  to={`/requests/${item.id}`}
                  className="block p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {item.title}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {item.reqId} · {item.client}
                      </p>
                    </div>
                    <div className="text-right">
                      <PriorityBadge priority={item.priority} />
                      {item.estimatedHours && (
                        <p className="text-xs text-gray-400 mt-1">
                          예상 {item.estimatedHours}h
                        </p>
                      )}
                    </div>
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
